import React, { useEffect, useState, useCallback } from 'react';
import {
  Search, Plus, RefreshCw, Phone, Mail, MessageCircle, Users,
  Clock, CheckCircle, AlertCircle, ChevronRight, X, Star,
  Briefcase, TrendingUp, Trash2, GitMerge, Edit2, ExternalLink, Upload, Link2,
} from 'lucide-react';
import { contactsApi } from '../services/contactsApi';
import { CustomerContactMethod, CustomerFollowUp, CustomerProfile, DuplicateCandidate, GoogleContactSyncRun, GoogleDiscoveredAccount, GoogleDuplicateRiskItem } from '../services/customerApi';
import api, { proxyGooglePhotoUrl } from '../services/api';
import { Customer } from '../types';

const STATUS_COLORS: Record<string, string> = {
  LEAD: 'bg-purple-100 text-purple-700',
  PROSPECT: 'bg-blue-100 text-blue-700',
  ACTIVE: 'bg-green-100 text-green-700',
  ON_HOLD: 'bg-yellow-100 text-yellow-700',
  INACTIVE: 'bg-gray-100 text-gray-500',
};

const STATUS_LABELS: Record<string, string> = {
  LEAD: 'Lead',
  PROSPECT: 'Prospect',
  ACTIVE: 'Active',
  ON_HOLD: 'On Hold',
  INACTIVE: 'Inactive',
};

const CHANNEL_ICON: Record<string, React.ReactNode> = {
  WHATSAPP: <MessageCircle className="h-3 w-3 text-green-500" />,
  EMAIL: <Mail className="h-3 w-3 text-blue-500" />,
  PHONE: <Phone className="h-3 w-3 text-gray-500" />,
};

function fmtDate(iso?: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function fmtMoney(n?: number | null) {
  if (n == null) return '—';
  return `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function normalizeEmail(value?: string | null) {
  const email = String(value || '').trim().toLowerCase();
  return email && email.includes('@') ? email : '';
}

function normalizePhoneKey(value?: string | null) {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length >= 7 ? digits.slice(-9) : '';
}

function readContactsHubHashIntent(): { contactId: string | null; search: string; edit: boolean } {
  if (typeof window === 'undefined') return { contactId: null, search: '', edit: false };
  const raw = String(window.location.hash || '').replace(/^#\//, '');
  const [pathPart = '', queryPart = ''] = raw.split('?');
  if (String(pathPart || '').trim().toUpperCase() !== 'CONTACTS') {
    return { contactId: null, search: '', edit: false };
  }
  const params = new URLSearchParams(queryPart);
  return {
    contactId: String(params.get('contact') || '').trim() || null,
    search: String(params.get('search') || '').trim(),
    edit: params.get('edit') === '1',
  };
}

function normalizeGoogleSnapshot(rawSnapshot: any): any {
  if (!rawSnapshot) return {};
  if (typeof rawSnapshot === 'string') {
    try {
      return JSON.parse(rawSnapshot);
    } catch {
      return {};
    }
  }
  return rawSnapshot;
}

function normalizeGoogleSourceSurface(source: any, rawSnapshot?: any): string {
  return String(
    source?.sourceSurface ||
    source?.source?.sourceSurface ||
    rawSnapshot?.sourceSurface ||
    rawSnapshot?.raw?.metadata?.sources?.[0]?.type ||
    ''
  ).trim().toUpperCase();
}

function extractPreferredGooglePhotoUrl(source: any, rawSnapshot: any, explicitPhotoUrl?: string | null): string {
  const normalized = normalizeGoogleSnapshot(rawSnapshot);
  const sourceSurface = normalizeGoogleSourceSurface(source, normalized);
  const photoEntries = [
    ...(Array.isArray(normalized.photos) ? normalized.photos : []),
    ...(Array.isArray(normalized.raw?.photos) ? normalized.raw.photos : []),
  ];
  const nonDefaultEntry = photoEntries.find((photo: any) => {
    const url = String(photo?.url || '').trim();
    return url && photo?.default !== true;
  });
  if (nonDefaultEntry?.url) {
    return String(nonDefaultEntry.url).trim();
  }
  const fallbackEntry = photoEntries.find((photo: any) => String(photo?.url || '').trim());
  if (fallbackEntry?.url && sourceSurface === 'CONTACT') {
    return String(fallbackEntry.url).trim();
  }
  if (fallbackEntry) {
    return '';
  }
  return String(explicitPhotoUrl || normalized.photoUrl || '').trim();
}

function getGoogleSourcePhotoUrl(source: any): string {
  const rawSnapshot = normalizeGoogleSnapshot(source?.source?.rawSnapshot);
  const preferredPhotoUrl = extractPreferredGooglePhotoUrl(
    source,
    rawSnapshot,
    source?.source?.photoUrl || source?.photoUrl || rawSnapshot.photoUrl
  );
  const cacheKey = [
    source?.id,
    source?.source?.externalId,
    source?.source?.lastSyncedAt,
    rawSnapshot.photoUrl,
  ].map((value) => String(value || '').trim()).filter(Boolean).join(':');

  return preferredPhotoUrl ? proxyGooglePhotoUrl(preferredPhotoUrl, cacheKey) : '';
}

function getGoogleSourcePriority(source: any): number {
  const rawSnapshot = normalizeGoogleSnapshot(source?.source?.rawSnapshot);
  const sourceSurface = normalizeGoogleSourceSurface(source, rawSnapshot);
  const hasPhoto = Boolean(
    extractPreferredGooglePhotoUrl(
      source,
      rawSnapshot,
      source?.source?.photoUrl || source?.photoUrl || rawSnapshot.photoUrl
    )
  );
  let score = 0;
  if (sourceSurface === 'CONTACT') score += 100;
  else if (sourceSurface === 'OTHER_CONTACT') score += 40;
  if (hasPhoto) score += 20;
  if (source?.isPrimarySource) score += 10;
  return score;
}

function selectPrimaryGoogleSource(externalSources: any[]): any | null {
  if (!Array.isArray(externalSources) || externalSources.length === 0) return null;
  return [...externalSources].sort((a, b) => getGoogleSourcePriority(b) - getGoogleSourcePriority(a))[0] || null;
}

function getGoogleSourcePrimaryName(source: any, fallbackName: string): string {
  const rawSnapshot = normalizeGoogleSnapshot(source?.source?.rawSnapshot);
  return String(
    source?.source?.primaryName ||
    rawSnapshot.primaryName ||
    rawSnapshot.displayName ||
    rawSnapshot.raw?.names?.[0]?.displayName ||
    source?.source?.primaryName ||
    source?.primaryName ||
    fallbackName ||
    '?'
  ).trim() || fallbackName || '?';
}

function summarizeSyncRuns(runs: GoogleContactSyncRun[]) {
  if (!runs.length) return 'No Google sync run recorded yet.';
  const latest = runs[0];
  const mode = latest.isDryRun ? 'dry run' : latest.syncType.toLowerCase();
  return [
    `${latest.accountKey} ${mode}`,
    latest.status.toLowerCase(),
    `scanned ${latest.scannedCount}`,
    `linked ${latest.linkedCustomerCount}`,
    `created ${latest.createdCustomerCount}`,
    latest.duplicateRiskCount ? `duplicate risk ${latest.duplicateRiskCount}` : null,
  ].filter(Boolean).join(' · ');
}

// ─────────────────────────────────────────────────────────────────────────────
// Customer List Row
// ─────────────────────────────────────────────────────────────────────────────

interface ListRowProps {
  customer: Customer;
  selected: boolean;
  onClick: () => void;
}

const ListRow: React.FC<ListRowProps> = ({ customer, selected, onClick }) => {
  const openWA = (customer as any)._count?.WhatsAppThreads || 0;
  const openEmail = (customer as any)._count?.EmailThreads || 0;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors flex items-center gap-3 ${selected ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''}`}
    >
      {/* Avatar */}
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
        {customer.name.charAt(0).toUpperCase()}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 text-sm truncate">{customer.name}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${STATUS_COLORS[customer.status] || STATUS_COLORS.INACTIVE}`}>
            {STATUS_LABELS[customer.status] || customer.status}
          </span>
        </div>
        <div className="text-xs text-gray-500 truncate mt-0.5">
          {customer.company || customer.email || customer.phoneNumber || 'No contact info'}
        </div>
      </div>

      {/* Badges */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {openWA > 0 && (
          <span className="flex items-center gap-0.5 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
            <MessageCircle className="h-2.5 w-2.5" />{openWA}
          </span>
        )}
        {openEmail > 0 && (
          <span className="flex items-center gap-0.5 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
            <Mail className="h-2.5 w-2.5" />{openEmail}
          </span>
        )}
        {(customer as any).lastContactAt && (
          <span className="text-[10px] text-gray-400">{fmtDate((customer as any).lastContactAt)}</span>
        )}
        <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
      </div>
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Add Follow-up Modal
// ─────────────────────────────────────────────────────────────────────────────

interface AddFollowUpProps {
  customerId: string;
  onSave: (f: CustomerFollowUp) => void;
  onClose: () => void;
}

const AddFollowUpModal: React.FC<AddFollowUpProps> = ({ customerId, onSave, onClose }) => {
  const [note, setNote] = useState('');
  const [dueAt, setDueAt] = useState(() => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    return t.toISOString().slice(0, 16);
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      const f = await contactsApi.addFollowUp(customerId, { note: note.trim(), dueAt: new Date(dueAt).toISOString() });
      onSave(f);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl p-5 w-96" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Schedule Follow-up</h3>
          <button onClick={onClose}><X className="h-4 w-4 text-gray-400" /></button>
        </div>
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="What needs to happen?"
          className="w-full border border-gray-200 rounded-lg p-2.5 text-sm resize-none h-20 focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
        <div className="mt-3">
          <label className="text-xs text-gray-500 mb-1 block">Due date & time</label>
          <input
            type="datetime-local"
            value={dueAt}
            onChange={e => setDueAt(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button
            onClick={save}
            disabled={saving || !note.trim()}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Add Contact Method Modal
// ─────────────────────────────────────────────────────────────────────────────

interface AddContactMethodProps {
  customerId: string;
  onSave: (m: CustomerContactMethod) => void;
  onClose: () => void;
}

const AddContactMethodModal: React.FC<AddContactMethodProps> = ({ customerId, onSave, onClose }) => {
  const [type, setType] = useState<'PHONE' | 'EMAIL' | 'WHATSAPP' | 'OTHER'>('PHONE');
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!value.trim()) return;
    setSaving(true);
    try {
      const m = await contactsApi.addContactMethod(customerId, { type, value: value.trim(), label: label.trim() || undefined, isPrimary });
      onSave(m);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl p-5 w-96" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Add Contact Method</h3>
          <button onClick={onClose}><X className="h-4 w-4 text-gray-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Type</label>
            <select value={type} onChange={e => setType(e.target.value as any)} className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400">
              <option value="PHONE">Phone</option>
              <option value="WHATSAPP">WhatsApp</option>
              <option value="EMAIL">Email</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Value</label>
            <input value={value} onChange={e => setValue(e.target.value)} placeholder={type === 'EMAIL' ? 'email@example.com' : '+1 242 555 0100'} className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Label (optional)</label>
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Personal, Work, Assistant" className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={isPrimary} onChange={e => setIsPrimary(e.target.checked)} className="rounded" />
            <span className="text-gray-700">Set as primary</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button onClick={save} disabled={saving || !value.trim()} className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg disabled:opacity-50">
            {saving ? 'Saving…' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Quick Create Customer Modal (used inline)
// ─────────────────────────────────────────────────────────────────────────────

interface QuickCreateProps {
  onCreated: (c: Customer) => void;
  onClose: () => void;
  prefill?: { name?: string; phone?: string; email?: string };
}

export const QuickCreateCustomerModal: React.FC<QuickCreateProps> = ({ onCreated, onClose, prefill }) => {
  const [name, setName] = useState(prefill?.name || '');
  const [phone, setPhone] = useState(prefill?.phone || '');
  const [email, setEmail] = useState(prefill?.email || '');
  const [company, setCompany] = useState('');
  const [saving, setSaving] = useState(false);
  const [duplicates, setDuplicates] = useState<DuplicateCandidate[]>([]);
  const [checkingDup, setCheckingDup] = useState(false);

  const checkDup = useCallback(async () => {
    if (!name && !phone && !email) return;
    setCheckingDup(true);
    try {
      const dups = await contactsApi.checkDuplicates({ name: name || undefined, phone: phone || undefined, email: email || undefined });
      setDuplicates(dups);
    } finally {
      setCheckingDup(false);
    }
  }, [name, phone, email]);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const c = await contactsApi.quickCreate({ name: name.trim(), phoneNumber: phone.trim() || undefined, email: email.trim() || undefined, company: company.trim() || undefined });
      onCreated(c);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl p-5 w-[420px]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">New Contact</h3>
          <button onClick={onClose}><X className="h-4 w-4 text-gray-400" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Full Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} onBlur={checkDup} placeholder="John Smith" className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Phone / WhatsApp</label>
              <input value={phone} onChange={e => setPhone(e.target.value)} onBlur={checkDup} placeholder="+1 242 555 0100" className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Email</label>
              <input value={email} onChange={e => setEmail(e.target.value)} onBlur={checkDup} placeholder="john@example.com" className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Company</label>
            <input value={company} onChange={e => setCompany(e.target.value)} placeholder="ABC Corp (optional)" className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
          </div>
        </div>

        {/* Duplicate warning */}
        {duplicates.length > 0 && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs font-medium text-amber-800 mb-2">Possible matches found:</p>
            {duplicates.map(d => (
              <div key={d.id} className="flex items-center justify-between text-xs text-amber-700 py-1">
                <span>{d.name} {d.email ? `· ${d.email}` : ''} {d.phoneNumber ? `· ${d.phoneNumber}` : ''}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${d.confidence === 'HIGH' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{d.confidence}</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button onClick={save} disabled={saving || !name.trim()} className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg disabled:opacity-50 flex items-center gap-1.5">
            {saving ? <><RefreshCw className="h-3 w-3 animate-spin" /> Saving…</> : <><Plus className="h-3 w-3" /> Create Contact</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Customer Profile Panel (right side)
// ─────────────────────────────────────────────────────────────────────────────

interface ProfilePanelProps {
  customerId: string;
  onClose: () => void;
  onNavigateToWA?: (threadId: string) => void;
  onNavigateToEmail?: (threadId: string) => void;
  onNavigateToQuote?: (quoteId: string) => void;
  onMergeRequest?: () => void;
  onEditRequest?: (customer: Customer) => void;
}

const ProfilePanel: React.FC<ProfilePanelProps> = ({ customerId, onClose, onNavigateToWA, onNavigateToEmail, onNavigateToQuote, onMergeRequest, onEditRequest }) => {
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddMethod, setShowAddMethod] = useState(false);
  const [showAddFollowUp, setShowAddFollowUp] = useState(false);
  const [editingStatus, setEditingStatus] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [googleSyncing, setGoogleSyncing] = useState(false);
  const [googleAccounts, setGoogleAccounts] = useState<GoogleDiscoveredAccount[]>([]);
  const [selectedGoogleSource, setSelectedGoogleSource] = useState<any | null>(null);
  const [googleSyncRuns, setGoogleSyncRuns] = useState<GoogleContactSyncRun[]>([]);
  const [googleDuplicateRisks, setGoogleDuplicateRisks] = useState<GoogleDuplicateRiskItem[]>([]);
  const [googleSyncStatus, setGoogleSyncStatus] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nextProfile, nextGoogleAccounts, nextGoogleRuns, duplicateRiskData] = await Promise.all([
        contactsApi.getProfile(customerId),
        contactsApi.getGoogleAccounts().catch(() => []),
        contactsApi.getGoogleSyncRuns({ limit: 12 }).catch(() => []),
        contactsApi.getGoogleDuplicateRisks({ limit: 150 }).catch(() => ({ risks: [], customers: [] })),
      ]);
      const customerEmails = new Set([
        normalizeEmail(nextProfile.customer.email),
        ...nextProfile.contactMethods.filter(method => method.type === 'EMAIL').map(method => normalizeEmail(method.value)),
      ].filter(Boolean));
      const customerPhones = new Set([
        normalizePhoneKey(nextProfile.customer.phoneNumber),
        ...nextProfile.contactMethods
          .filter(method => method.type === 'PHONE' || method.type === 'WHATSAPP')
          .map(method => normalizePhoneKey(method.value)),
      ].filter(Boolean));
      const relevantDuplicateRisks = (duplicateRiskData.risks || []).filter((risk) => {
        if (risk.customerIds.includes(customerId)) return true;
        const hasEmailMatch = (risk.emails || []).some(email => customerEmails.has(normalizeEmail(email)));
        const hasPhoneMatch = (risk.phones || []).some(phone => customerPhones.has(normalizePhoneKey(phone)));
        return hasEmailMatch || hasPhoneMatch;
      });
      setProfile(nextProfile);
      setGoogleAccounts(nextGoogleAccounts);
      setGoogleSyncRuns(nextGoogleRuns);
      setGoogleDuplicateRisks(relevantDuplicateRisks);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const completeFollowUp = async (followUpId: string) => {
    if (!profile) return;
    await contactsApi.updateFollowUp(customerId, followUpId, { completedAt: new Date().toISOString() });
    setProfile(p => p ? { ...p, followUps: p.followUps.map(f => f.id === followUpId ? { ...f, completedAt: new Date().toISOString() } : f) } : p);
  };

  const deleteContactMethod = async (methodId: string) => {
    await contactsApi.deleteContactMethod(customerId, methodId);
    setProfile(p => p ? { ...p, contactMethods: p.contactMethods.filter(m => m.id !== methodId) } : p);
  };

  const changeStatus = async (status: string) => {
    if (!profile) return;
    setSavingStatus(true);
    try {
      await contactsApi.update(customerId, { status: status as any });
      setProfile(p => p ? { ...p, customer: { ...p.customer, status: status as any } } : p);
      setEditingStatus(false);
    } finally {
      setSavingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!profile) return null;

  const { customer, contactMethods, followUps, whatsappThreads, emailThreads, quotes, missions } = profile;
  const externalSources = profile.externalSources || [];
  const primaryGoogleSource = selectPrimaryGoogleSource(externalSources);
  const googlePhotoUrl = getGoogleSourcePhotoUrl(primaryGoogleSource);
  const primaryGoogleName = getGoogleSourcePrimaryName(primaryGoogleSource, customer.name);
  const pendingFollowUps = followUps.filter(f => !f.completedAt);
  const overdueFollowUps = pendingFollowUps.filter(f => new Date(f.dueAt) < new Date());
  const googleEnabledAccounts = googleAccounts.filter(account => account.peopleReadEnabled);
  const googleEnabledAndSyncableAccounts = googleAccounts.filter(account => account.peopleReadEnabled && account.syncEnabled);
  const latestGoogleRun = googleSyncRuns[0] || null;
  const latestGoogleRunSummary = summarizeSyncRuns(googleSyncRuns);
  const latestGoogleSyncErrors = googleSyncRuns.flatMap(run => run.sampleErrors || []).slice(0, 3);
  const googleAccountErrors = googleAccounts
    .filter(account => String(account.syncState?.lastError || '').trim())
    .map(account => ({ accountKey: account.accountKey, error: String(account.syncState?.lastError || '').trim() }))
    .slice(0, 3);

  const syncGoogleContactsNow = async () => {
    setGoogleSyncing(true);
    try {
      setGoogleSyncStatus(null);
      const results = await contactsApi.syncGoogleContacts();
      if (!results.length) {
        if (!googleEnabledAccounts.length) {
          setGoogleSyncStatus('No Google account has People read enabled for contact sync.');
        } else if (!googleEnabledAndSyncableAccounts.length) {
          setGoogleSyncStatus('Google contact sync is skipped because all People-enabled accounts are disabled for sync.');
        } else {
          setGoogleSyncStatus('Google contact sync returned no run results.');
        }
      } else {
        setGoogleSyncStatus(summarizeSyncRuns(results));
      }
      await load();
    } finally {
      setGoogleSyncing(false);
    }
  };

  // Build combined activity timeline
  const timeline: Array<{ type: string; id: string; label: string; sub: string; at: string; status?: string; flagged?: boolean }> = [
    ...whatsappThreads.map(t => ({
      type: 'whatsapp', id: t.id,
      label: `WhatsApp${t.Number?.label ? ` (${t.Number.label})` : ''}`,
      sub: t.status.replace(/_/g, ' '),
      at: t.lastMessageAt,
      status: t.status,
      flagged: t.needsReply || t.isOverdue,
    })),
    ...emailThreads.map(t => ({
      type: 'email', id: t.id,
      label: `Email${t.subject ? ` — ${t.subject}` : ''}`,
      sub: t.status,
      at: t.lastMessageAt,
      status: t.status,
      customerEmail: t.customerEmail,
      subject: t.subject,
    })),
    ...quotes.map(q => ({
      type: 'quote', id: q.id,
      label: `Quote${q.legs?.[0] ? ` ${q.legs[0].departureAirport}→${q.legs[0].arrivalAirport}` : ''}`,
      sub: `${fmtMoney(q.totalUSD)} · ${q.status.replace(/_/g, ' ')}`,
      at: q.createdAt,
      status: q.status,
    })),
    ...missions.map(m => ({
      type: 'mission', id: m.id,
      label: `✈ Mission${m.departureAirport ? ` ${m.departureAirport}→${m.arrivalAirport}` : ''}`,
      sub: `${fmtMoney(m.totalUSD)} · ${m.status.replace(/_/g, ' ')}`,
      at: m.createdAt,
      status: m.status,
    })),
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  const typeIcon = (type: string) => {
    if (type === 'whatsapp') return <MessageCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />;
    if (type === 'email') return <Mail className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />;
    if (type === 'quote') return <Briefcase className="h-3.5 w-3.5 text-indigo-500 flex-shrink-0" />;
    return <TrendingUp className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />;
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-white">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white text-lg font-bold flex-shrink-0 overflow-hidden">
          {googlePhotoUrl ? (
            <img
              src={googlePhotoUrl}
              alt={customer.name}
              className="h-full w-full object-cover"
              referrerPolicy="no-referrer"
            />
          ) : (
            primaryGoogleName.charAt(0).toUpperCase()
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-base font-semibold text-gray-900 truncate">{customer.name}</h2>
            {editingStatus ? (
              <select
                autoFocus
                value={customer.status}
                onChange={e => changeStatus(e.target.value)}
                onBlur={() => setEditingStatus(false)}
                className="text-xs border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none"
                disabled={savingStatus}
              >
                {['LEAD', 'PROSPECT', 'ACTIVE', 'ON_HOLD', 'INACTIVE'].map(s => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            ) : (
              <button onClick={() => setEditingStatus(true)} className={`text-xs px-1.5 py-0.5 rounded font-medium ${STATUS_COLORS[customer.status] || STATUS_COLORS.INACTIVE}`}>
                {STATUS_LABELS[customer.status] || customer.status}
              </button>
            )}
            {externalSources.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                <Link2 className="h-2.5 w-2.5" />
                Google Linked
              </span>
            )}
          </div>
          {customer.company && <p className="text-xs text-gray-500 mt-0.5">{customer.company}</p>}
          {customer.zohoId && <p className="text-[10px] text-gray-400 mt-0.5">Zoho client</p>}
        </div>
        {onEditRequest && (
          <button onClick={() => onEditRequest(customer)} className="text-gray-400 hover:text-blue-600 flex-shrink-0" title="Edit contact">
            <Edit2 className="h-4 w-4" />
          </button>
        )}
        {onMergeRequest && (
          <button onClick={onMergeRequest} className="text-gray-400 hover:text-indigo-600 flex-shrink-0" title="Merge duplicate">
            <GitMerge className="h-4 w-4" />
          </button>
        )}
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">

        {/* Open items strip */}
        {(whatsappThreads.filter(t => t.status === 'OPEN' || t.status === 'WAITING_CLIENT').length > 0 ||
          emailThreads.filter(t => t.status === 'OPEN').length > 0 ||
          quotes.filter(q => q.status === 'PENDING' || q.status === 'SENT').length > 0 ||
          overdueFollowUps.length > 0) && (
          <div className="mx-4 mt-4 bg-amber-50 border border-amber-100 rounded-lg p-3 space-y-1.5">
            <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide mb-2">Open Right Now</p>
            {whatsappThreads.filter(t => ['OPEN', 'WAITING_CLIENT'].includes(t.status)).map(t => (
              <button key={t.id} onClick={() => onNavigateToWA?.(t.id)} className="flex items-center gap-2 text-xs text-amber-800 hover:text-amber-900 w-full text-left">
                <MessageCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                <span>WhatsApp{t.Number?.label ? ` (${t.Number.label})` : ''} · {t.status.replace(/_/g, ' ')}</span>
                {t.needsReply && <span className="ml-auto text-[10px] bg-red-100 text-red-600 px-1 rounded">Needs reply</span>}
              </button>
            ))}
            {emailThreads.filter(t => t.status === 'OPEN').map(t => (
              <button key={t.id} onClick={() => onNavigateToEmail?.(t.customerEmail || t.subject || t.id)} className="flex items-center gap-2 text-xs text-amber-800 hover:text-amber-900 w-full text-left">
                <Mail className="h-3 w-3 text-blue-500 flex-shrink-0" />
                <span>{t.subject || 'Email thread'}</span>
              </button>
            ))}
            {quotes.filter(q => ['PENDING', 'SENT', 'APPROVED'].includes(q.status)).map(q => (
              <button key={q.id} onClick={() => onNavigateToQuote?.(q.id)} className="flex items-center gap-2 text-xs text-amber-800 hover:text-amber-900 w-full text-left">
                <Briefcase className="h-3 w-3 text-indigo-500 flex-shrink-0" />
                <span>Quote {q.legs?.[0] ? `${q.legs[0].departureAirport}→${q.legs[0].arrivalAirport}` : ''} · {fmtMoney(q.totalUSD)}</span>
              </button>
            ))}
            {overdueFollowUps.map(f => (
              <div key={f.id} className="flex items-center gap-2 text-xs text-red-700">
                <Clock className="h-3 w-3 flex-shrink-0" />
                <span>Follow-up overdue: {f.note}</span>
              </div>
            ))}
          </div>
        )}

        {/* Google Sources */}
        <div className="mx-4 mt-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Google Sources</p>
            <button
              onClick={syncGoogleContactsNow}
              disabled={googleSyncing}
              className="text-[10px] text-blue-600 hover:text-blue-700 disabled:opacity-50 flex items-center gap-1"
            >
              <RefreshCw className={`h-3 w-3 ${googleSyncing ? 'animate-spin' : ''}`} />
              Sync Google Contacts
            </button>
          </div>
          {googleSyncStatus && (
            <div className="mb-2 rounded-lg border border-blue-100 bg-blue-50 px-2.5 py-2 text-[11px] text-blue-700">
              {googleSyncStatus}
            </div>
          )}
          <div className="space-y-2">
            {externalSources.map(source => (
              <button key={source.id} onClick={() => setSelectedGoogleSource(source)} className="w-full rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-left hover:border-blue-200 hover:bg-blue-50/40">
                <div className="flex items-center gap-2">
                  {getGoogleSourcePhotoUrl(source) ? (
                    <img
                      src={getGoogleSourcePhotoUrl(source)}
                      alt={source.accountKey}
                      className="h-7 w-7 rounded-full object-cover border border-white shadow-sm"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-[10px] font-semibold text-gray-600 shadow-sm">
                      {getGoogleSourcePrimaryName(source, customer.name).charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-semibold text-gray-700">
                    {source.accountKey}
                  </span>
                  <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
                    {source.sourceSurface === 'OTHER_CONTACT' ? 'Other Contact' : 'Saved Contact'}
                  </span>
                  {source.matchConfidence && (
                    <span className="text-[10px] text-emerald-700">
                      {source.matchConfidence}
                    </span>
                  )}
                  {source.source?.deletedAt && (
                    <span className="ml-auto rounded bg-red-50 px-1.5 py-0.5 text-[10px] text-red-600">
                      Deleted in Google
                    </span>
                  )}
                </div>
                <p className="mt-1 text-[11px] text-gray-600">
                  {googleAccounts.find(account => account.accountKey === source.accountKey)?.mailboxEmail || 'Google mailbox'}
                </p>
                <div className="mt-1 space-y-0.5 text-[10px] text-gray-500">
                  <p>Reason: {source.matchReason || 'linked-source'}</p>
                  <p>Last synced: {fmtDate(source.source?.lastSyncedAt || null)}</p>
                  <p>External ID: {source.source?.externalId || '—'}</p>
                </div>
              </button>
            ))}
            {externalSources.length === 0 && (
              <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-2.5 text-xs text-gray-500">
                No linked Google contact source yet.
                {googleEnabledAccounts.length > 0 ? ` ${googleEnabledAccounts.length} Google account(s) are People-enabled.` : ' No sync-capable Google account detected.'}
                {!googleEnabledAndSyncableAccounts.length && googleEnabledAccounts.length > 0 ? ' Sync is currently disabled for those accounts.' : ''}
              </div>
            )}
            {externalSources.length === 0 && latestGoogleRun && (
              <div className="rounded-lg border border-gray-200 bg-white p-2.5 text-[11px] text-gray-600">
                <p className="font-medium text-gray-800">Latest sync result</p>
                <p className="mt-1">{latestGoogleRunSummary}</p>
                {latestGoogleRun.errorMessage && (
                  <p className="mt-1 text-red-600">{latestGoogleRun.errorMessage}</p>
                )}
              </div>
            )}
            {externalSources.length === 0 && googleDuplicateRisks.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-800">
                <p className="font-medium">Google duplicate-risk review is blocking auto-link for this contact.</p>
                <p className="mt-1">
                  {googleDuplicateRisks.length} pending Google source{googleDuplicateRisks.length === 1 ? '' : 's'} matched this customer alongside other candidate customers.
                </p>
              </div>
            )}
            {externalSources.length === 0 && latestGoogleSyncErrors.length > 0 && (
              <div className="rounded-lg border border-red-100 bg-red-50 p-2.5 text-[11px] text-red-700">
                <p className="font-medium">Recent Google sync errors</p>
                <div className="mt-1 space-y-1">
                  {latestGoogleSyncErrors.map((item, index) => (
                    <p key={`${item.externalId}-${index}`}>{item.externalId}: {item.message}</p>
                  ))}
                </div>
              </div>
            )}
            {externalSources.length === 0 && latestGoogleSyncErrors.length === 0 && googleAccountErrors.length > 0 && (
              <div className="rounded-lg border border-red-100 bg-red-50 p-2.5 text-[11px] text-red-700">
                <p className="font-medium">Google account issues</p>
                <div className="mt-1 space-y-1">
                  {googleAccountErrors.map((item) => (
                    <p key={item.accountKey}>{item.accountKey}: {item.error}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Financials (Zoho) */}
        {(customer.zohoInvoicesTotal != null || customer.outstandingBalance != null) && (
          <div className="mx-4 mt-4">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Financials</p>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <p className="text-xs font-semibold text-gray-900">{fmtMoney(customer.zohoInvoicesTotal)}</p>
                <p className="text-[10px] text-gray-500">Invoiced</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <p className={`text-xs font-semibold ${(customer.outstandingBalance || 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}>{fmtMoney(customer.outstandingBalance)}</p>
                <p className="text-[10px] text-gray-500">Balance</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <p className="text-xs font-semibold text-gray-900">{customer.zohoInvoicesCount ?? 0}</p>
                <p className="text-[10px] text-gray-500">Invoices</p>
              </div>
            </div>
            {customer.charterDiscount > 0 && <p className="text-[10px] text-gray-500 mt-1.5">Charter discount: {customer.charterDiscount}%</p>}
            {customer.scheduleDiscount > 0 && <p className="text-[10px] text-gray-500">Scheduled discount: {customer.scheduleDiscount}%</p>}
          </div>
        )}

        {/* Contact Methods */}
        <div className="mx-4 mt-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Contact Methods</p>
            <button onClick={() => setShowAddMethod(true)} className="text-[10px] text-blue-600 hover:text-blue-700 flex items-center gap-0.5">
              <Plus className="h-3 w-3" />Add
            </button>
          </div>

          {/* Primary phone/email from customer record */}
          <div className="space-y-1.5">
            {customer.phoneNumber && (
              <div className="flex items-center gap-2 text-xs text-gray-700">
                <Phone className="h-3 w-3 text-gray-400 flex-shrink-0" />
                <span>{customer.phoneNumber}</span>
                <span className="text-[10px] text-gray-400">Primary</span>
              </div>
            )}
            {customer.email && (
              <div className="flex items-center gap-2 text-xs text-gray-700">
                <Mail className="h-3 w-3 text-gray-400 flex-shrink-0" />
                <span className="truncate">{customer.email}</span>
                <span className="text-[10px] text-gray-400">Primary</span>
              </div>
            )}
            {contactMethods.map(m => (
              <div key={m.id} className="flex items-center gap-2 text-xs text-gray-700 group">
                {CHANNEL_ICON[m.type] || <Phone className="h-3 w-3 text-gray-400 flex-shrink-0" />}
                <span className="truncate flex-1">{m.value}</span>
                {m.label && <span className="text-[10px] text-gray-400">{m.label}</span>}
                {m.isPrimary && <Star className="h-2.5 w-2.5 text-yellow-400 flex-shrink-0" />}
                <button onClick={() => deleteContactMethod(m.id)} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 flex-shrink-0">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
            {!customer.phoneNumber && !customer.email && contactMethods.length === 0 && (
              <p className="text-xs text-gray-400 italic">No contact methods yet</p>
            )}
          </div>
        </div>

        {/* Follow-ups */}
        <div className="mx-4 mt-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Follow-ups</p>
            <button onClick={() => setShowAddFollowUp(true)} className="text-[10px] text-blue-600 hover:text-blue-700 flex items-center gap-0.5">
              <Plus className="h-3 w-3" />Schedule
            </button>
          </div>
          <div className="space-y-1.5">
            {pendingFollowUps.map(f => {
              const isOver = new Date(f.dueAt) < new Date();
              return (
                <div key={f.id} className={`flex items-start gap-2 text-xs rounded-lg p-2 ${isOver ? 'bg-red-50' : 'bg-gray-50'}`}>
                  <Clock className={`h-3 w-3 flex-shrink-0 mt-0.5 ${isOver ? 'text-red-500' : 'text-gray-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`${isOver ? 'text-red-700' : 'text-gray-700'}`}>{f.note}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{new Date(f.dueAt).toLocaleString()}</p>
                  </div>
                  <button onClick={() => completeFollowUp(f.id)} className="text-green-600 hover:text-green-700 flex-shrink-0" title="Mark done">
                    <CheckCircle className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
            {followUps.filter(f => f.completedAt).slice(0, 3).map(f => (
              <div key={f.id} className="flex items-start gap-2 text-xs text-gray-400 opacity-60">
                <CheckCircle className="h-3 w-3 flex-shrink-0 mt-0.5 text-green-400" />
                <span className="line-through">{f.note}</span>
              </div>
            ))}
            {pendingFollowUps.length === 0 && followUps.length === 0 && (
              <p className="text-xs text-gray-400 italic">No follow-ups scheduled</p>
            )}
          </div>
        </div>

        {/* Activity Timeline */}
        <div className="mx-4 mt-4 mb-6">
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Activity Timeline</p>
          {timeline.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No activity yet</p>
          ) : (
            <div className="space-y-1.5">
              {timeline.map((item, i) => (
                <button
                  key={`${item.type}-${item.id}-${i}`}
                  onClick={() => {
                    if (item.type === 'whatsapp') onNavigateToWA?.(item.id);
                    if (item.type === 'email') onNavigateToEmail?.((item as any).customerEmail || (item as any).subject || item.id);
                    if (item.type === 'quote') onNavigateToQuote?.(item.id);
                  }}
                  className="w-full text-left flex items-start gap-2 text-xs hover:bg-gray-50 rounded px-1 py-1.5 group"
                >
                  <span className="mt-0.5">{typeIcon(item.type)}</span>
                  <div className="flex-1 min-w-0">
                    <span className={`font-medium ${item.flagged ? 'text-red-600' : 'text-gray-800'}`}>{item.label}</span>
                    <span className="text-gray-400 ml-1.5">{item.sub}</span>
                  </div>
                  <span className="text-[10px] text-gray-400 flex-shrink-0">{fmtDate(item.at)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showAddMethod && (
        <AddContactMethodModal
          customerId={customerId}
          onSave={m => { setProfile(p => p ? { ...p, contactMethods: [...p.contactMethods, m] } : p); setShowAddMethod(false); }}
          onClose={() => setShowAddMethod(false)}
        />
      )}
      {showAddFollowUp && (
        <AddFollowUpModal
          customerId={customerId}
          onSave={f => { setProfile(p => p ? { ...p, followUps: [...p.followUps, f] } : p); setShowAddFollowUp(false); }}
          onClose={() => setShowAddFollowUp(false)}
        />
      )}
      {selectedGoogleSource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35" onClick={() => setSelectedGoogleSource(null)}>
          <div className="max-h-[80vh] w-[720px] overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Google Source Detail</h3>
                <p className="mt-1 text-xs text-gray-500">{selectedGoogleSource.accountKey} · {selectedGoogleSource.source?.externalId || 'No external id'}</p>
              </div>
              <button onClick={() => setSelectedGoogleSource(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4 overflow-y-auto px-5 py-4">
              <div className="flex items-center gap-3">
                {getGoogleSourcePhotoUrl(selectedGoogleSource) ? (
                  <img
                    src={getGoogleSourcePhotoUrl(selectedGoogleSource)}
                    alt={getGoogleSourcePrimaryName(selectedGoogleSource, customer.name)}
                    className="h-14 w-14 rounded-full object-cover border border-gray-200"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 text-lg font-bold text-white">
                    {getGoogleSourcePrimaryName(selectedGoogleSource, customer.name).charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">
                    {getGoogleSourcePrimaryName(selectedGoogleSource, customer.name)}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    {selectedGoogleSource.sourceSurface === 'OTHER_CONTACT' ? 'Other Contact' : 'Saved Contact'}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="rounded-lg bg-gray-50 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Match</p>
                  <p className="mt-1 text-gray-800">{selectedGoogleSource.matchConfidence || '—'} · {selectedGoogleSource.matchReason || '—'}</p>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Last Synced</p>
                  <p className="mt-1 text-gray-800">{fmtDate(selectedGoogleSource.source?.lastSyncedAt || null)}</p>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Deleted In Google</p>
                  <p className="mt-1 text-gray-800">{selectedGoogleSource.source?.deletedAt ? fmtDate(selectedGoogleSource.source.deletedAt) : 'No'}</p>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">ETag</p>
                  <p className="mt-1 break-all text-gray-800">{selectedGoogleSource.source?.etag || '—'}</p>
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Snapshot</p>
                <pre className="mt-2 max-h-[320px] overflow-auto whitespace-pre-wrap break-words text-[11px] text-gray-700">
                  {JSON.stringify(selectedGoogleSource.source?.rawSnapshot || {}, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main ContactsHub Page
// ─────────────────────────────────────────────────────────────────────────────

interface ContactsHubProps {
  customers: Customer[];
  setCustomers: React.Dispatch<React.SetStateAction<Customer[]>>;
  onNavigateToWA?: (threadId: string) => void;
  onNavigateToEmail?: (threadId: string) => void;
  onNavigateToQuote?: (quoteId: string) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Merge Duplicates Modal
// ─────────────────────────────────────────────────────────────────────────────

interface MergeModalProps {
  winner: Customer;
  customers: Customer[];
  onMerged: (winnerId: string, loserId: string) => void;
  onClose: () => void;
}

const MergeModal: React.FC<MergeModalProps> = ({ winner, customers, onMerged, onClose }) => {
  const [loserId, setLoserId] = useState('');
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState('');

  const loser = customers.find(c => c.id === loserId);

  const doMerge = async () => {
    if (!loserId) return;
    setMerging(true);
    setError('');
    try {
      await contactsApi.mergeContacts(winner.id, loserId);
      onMerged(winner.id, loserId);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl p-5 w-[480px]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <GitMerge className="h-4 w-4 text-indigo-600" />
            <h3 className="font-semibold text-gray-900">Merge Duplicate Contact</h3>
          </div>
          <button onClick={onClose}><X className="h-4 w-4 text-gray-400" /></button>
        </div>

        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4 text-xs text-blue-800">
          <p className="font-semibold mb-1">How merge works:</p>
          <p>All WhatsApp threads, emails, quotes, missions, contact methods and follow-ups from the <strong>duplicate</strong> will be moved to the <strong>winner</strong>. The duplicate is then permanently deleted.</p>
        </div>

        {/* Winner */}
        <div className="mb-3">
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Keep (Winner)</p>
          <div className="flex items-center gap-3 border border-green-200 bg-green-50 rounded-lg p-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center text-white text-sm font-bold">
              {winner.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">{winner.name}</p>
              <p className="text-xs text-gray-500">{winner.email || winner.phoneNumber || 'No contact info'}</p>
            </div>
            <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUS_COLORS[winner.status] || ''}`}>{STATUS_LABELS[winner.status]}</span>
          </div>
        </div>

        {/* Loser picker */}
        <div className="mb-4">
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Delete (Duplicate)</p>
          <select
            value={loserId}
            onChange={e => setLoserId(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
          >
            <option value="">Select the duplicate to remove…</option>
            {customers.filter(c => c.id !== winner.id).map(c => (
              <option key={c.id} value={c.id}>{c.name}{c.email ? ` · ${c.email}` : ''}{c.phoneNumber ? ` · ${c.phoneNumber}` : ''}</option>
            ))}
          </select>
          {loser && (
            <div className="flex items-center gap-3 border border-red-200 bg-red-50 rounded-lg p-3 mt-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-400 to-rose-600 flex items-center justify-center text-white text-sm font-bold">
                {loser.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm">{loser.name}</p>
                <p className="text-xs text-gray-500">{loser.email || loser.phoneNumber || 'No contact info'}</p>
              </div>
              <Trash2 className="h-4 w-4 text-red-400 flex-shrink-0" />
            </div>
          )}
        </div>

        {error && <p className="text-xs text-red-600 mb-3">{error}</p>}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button
            onClick={doMerge}
            disabled={merging || !loserId}
            className="px-4 py-1.5 bg-indigo-600 text-white text-sm rounded-lg disabled:opacity-50 flex items-center gap-1.5"
          >
            {merging ? <><RefreshCw className="h-3 w-3 animate-spin" />Merging…</> : <><GitMerge className="h-3 w-3" />Merge & Delete Duplicate</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── FullContactFormModal ─────────────────────────────────────────────────────
// Full Zoho-ready form for creating or editing a contact

interface FullContactFormProps {
  initial?: Partial<Customer>;
  onSaved: (c: Customer) => void;
  onClose: () => void;
}

const inputClass = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

export const FullContactFormModal: React.FC<FullContactFormProps> = ({ initial, onSaved, onClose }) => {
  const isEdit = !!initial?.id;

  const [customerType, setCustomerType] = useState<'BUSINESS' | 'INDIVIDUAL'>(
    (initial?.customerType as any) === 'INDIVIDUAL' ? 'INDIVIDUAL' : 'BUSINESS'
  );
  const [firstName, setFirstName] = useState(initial?.firstName || '');
  const [lastName, setLastName] = useState(initial?.lastName || '');
  const [displayName, setDisplayName] = useState(initial?.name || '');
  const [company, setCompany] = useState(initial?.company || '');
  const [phone, setPhone] = useState(initial?.phoneNumber || '');
  const [email, setEmail] = useState(initial?.email || '');
  const [tin, setTin] = useState(initial?.tin || '');
  const [vrn, setVrn] = useState(initial?.vrn || '');
  const [pushToZoho, setPushToZoho] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [zohoPushing, setZohoPushing] = useState(false);
  const [zohoResult, setZohoResult] = useState('');

  // Auto-fill display name from first+last or company
  useEffect(() => {
    if (isEdit) return; // don't override in edit mode
    if (customerType === 'INDIVIDUAL' && (firstName || lastName)) {
      setDisplayName(`${firstName} ${lastName}`.trim());
    } else if (customerType === 'BUSINESS' && company) {
      setDisplayName(company);
    }
  }, [firstName, lastName, company, customerType, isEdit]);

  const handleSave = async () => {
    if (!displayName.trim()) { setError('Display Name is required'); return; }
    setSaving(true); setError('');
    try {
      const payload: Partial<Customer> = {
        name: displayName.trim(),
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        company: company.trim() || undefined,
        phoneNumber: phone.trim() || undefined,
        email: email.trim() || undefined,
        tin: tin.trim() || undefined,
        vrn: vrn.trim() || undefined,
        customerType,
      };

      let saved: Customer;
      if (isEdit && initial?.id) {
        saved = await contactsApi.update(initial.id, payload);
      } else {
        saved = await contactsApi.create({ ...payload, name: payload.name! });
      }

      if (pushToZoho && !isEdit) {
        setZohoPushing(true);
        try {
          const result = await contactsApi.zohoPush(saved.id);
          saved = result.data;
          setZohoResult(`Synced to Zoho Books ✓ (ID: ${result.zohoContactId})`);
        } catch (ze: any) {
          setZohoResult(`Saved locally. Zoho sync failed: ${ze?.response?.data?.error || ze.message}`);
        } finally {
          setZohoPushing(false);
        }
        // Show result briefly then close
        setTimeout(() => onSaved(saved), 1500);
        return;
      }

      onSaved(saved);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">{isEdit ? 'Edit Contact' : 'New Contact'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Customer Type */}
          <div>
            <label className={labelClass}>Customer Type</label>
            <div className="flex gap-3">
              {(['BUSINESS', 'INDIVIDUAL'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setCustomerType(t)}
                  className={`flex-1 py-2 rounded-lg border text-sm font-medium transition ${
                    customerType === t ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >{t === 'BUSINESS' ? '🏢 Business' : '👤 Individual'}</button>
              ))}
            </div>
          </div>

          {/* Name row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>First Name</label>
              <input className={inputClass} placeholder="John" value={firstName} onChange={e => setFirstName(e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Last Name</label>
              <input className={inputClass} placeholder="Smith" value={lastName} onChange={e => setLastName(e.target.value)} />
            </div>
          </div>

          {/* Company (required for Business) */}
          {customerType === 'BUSINESS' && (
            <div>
              <label className={labelClass}>Company Name</label>
              <input className={inputClass} placeholder="ABC Corporation" value={company} onChange={e => setCompany(e.target.value)} />
            </div>
          )}

          {/* Display Name */}
          <div>
            <label className={labelClass}>Display Name <span className="text-red-500">*</span></label>
            <input
              className={inputClass}
              placeholder="How this contact appears everywhere"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
            />
            <p className="text-xs text-gray-400 mt-0.5">Auto-filled from name/company — you can override</p>
          </div>

          {/* Phone + Email */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Phone / WhatsApp</label>
              <input className={inputClass} placeholder="+255 700 000 000" value={phone} onChange={e => setPhone(e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Email</label>
              <input className={inputClass} type="email" placeholder="name@example.com" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
          </div>

          {/* TIN + VRN */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>TIN (Tax ID)</label>
              <input className={inputClass} placeholder="123-456-789" value={tin} onChange={e => setTin(e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>VRN (VAT Reg No.)</label>
              <input className={inputClass} placeholder="40-000000-A" value={vrn} onChange={e => setVrn(e.target.value)} />
            </div>
          </div>

          {/* Zoho push (only on create) */}
          {!isEdit && (
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={pushToZoho}
                onChange={e => setPushToZoho(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm font-medium text-gray-700">Also create in Zoho Books</span>
              <ExternalLink className="h-3.5 w-3.5 text-gray-400 ml-auto" />
            </label>
          )}

          {error && <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
          {zohoResult && <p className="text-xs text-blue-700 bg-blue-50 rounded-lg px-3 py-2">{zohoResult}</p>}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || zohoPushing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-60"
          >
            {(saving || zohoPushing) && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
            {zohoPushing ? 'Syncing to Zoho…' : saving ? 'Saving…' : isEdit ? 'Save Changes' : '+ Create Contact'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── CSV Import Modal ──────────────────────────────────────────────────────────

// Simple CSV parser that handles quoted fields
function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  const safeText = text.replace(/^\uFEFF/, '');
  const lines = safeText.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const parseRow = (line: string): string[] => {
    const cols: string[] = [];
    let cur = '';
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
        else { inQ = !inQ; }
      } else if (ch === ',' && !inQ) { cols.push(cur); cur = ''; }
      else { cur += ch; }
    }
    cols.push(cur);
    return cols;
  };
  const headers = parseRow(lines[0] || '');
  const rows = lines.slice(1).filter(l => l.trim()).map(parseRow);
  return { headers, rows };
}

// MA-OS fields available for mapping
const MAOS_FIELDS = [
  { key: 'name',         label: 'Display Name *', required: true },
  { key: 'firstName',    label: 'First Name' },
  { key: 'lastName',     label: 'Last Name' },
  { key: 'company',      label: 'Company / Organization' },
  { key: 'email',        label: 'Email' },
  { key: 'phoneNumber',  label: 'Phone / WhatsApp' },
  { key: 'tin',          label: 'TIN (Tax ID)' },
  { key: 'vrn',          label: 'VRN (VAT Reg No.)' },
] as const;

type MaosFieldKey = typeof MAOS_FIELDS[number]['key'];

// Auto-guess mapping from CSV header names
function guessMapping(headers: string[]): Record<MaosFieldKey, string> {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9 ]/g, '').trim();
  const findExact = (...candidates: string[]) =>
    candidates.reduce<string>((f, k) => f || headers.find(h => norm(h) === norm(k)) || '', '');
  const findContains = (...candidates: string[]) =>
    candidates.reduce<string>((f, k) => f || headers.find(h => norm(h).includes(norm(k))) || '', '');

  return {
    // Google Contacts: "Name" = full display name. Outlook: "Full Name". Keep it simple.
    name:        findExact('name', 'full name', 'display name', 'contact name', 'company / organization')
                   || findContains('company organization'),
    // Google: "Given Name". Outlook: "First Name".
    firstName:   findExact('given name', 'first name', 'firstname', 'given'),
    // Google: "Family Name". Outlook: "Last Name".
    lastName:    findExact('family name', 'last name', 'lastname', 'surname'),
    // Google: "Organization 1 - Name". Outlook: "Company".
    company:     findExact('organization 1 - name', 'organization', 'company', 'company name', 'company / organization')
                   || findContains('organization 1', 'company organization'),
    // Google: "E-mail 1 - Value". Outlook: "E-mail Address".
    email:       findExact('e-mail 1 - value', 'email 1 - value', 'e-mail address', 'email address', 'e-mail', 'email')
                   || findContains('e-mail 1 - value', 'email 1 - value', 'e-mail', 'email'),
    // Google: "Phone 1 - Value". Outlook: "Primary Phone" or "Mobile Phone".
    phoneNumber: findExact('phone 1 - value', 'primary phone', 'mobile phone', 'phone number', 'phone / whatsapp', 'phone', 'whatsapp')
                   || findContains('phone 1 - value', 'phone whatsapp', 'phone', 'whatsapp'),
    tin:         findExact('tin') || findContains('tax id'),
    vrn:         findExact('vrn') || findContains('vat reg'),
  };
}

// Returns true if a value looks like garbage data (address, postal code, floor/room number)
const STREET_KEYWORDS = /\b(street|road|rd|avenue|ave|boulevard|blvd|drive|dr|lane|ln|close|terrace|crescent|place|highway|way|floor|room|suite|block|building|plaza|p\.?o\.?\s*box)\b/i;

function normalizeImportValue(val: string): string {
  const v = String(val || '').trim();
  if (!v) return '';
  if (v === '-' || v === '—' || /^[-.\s_]+$/.test(v)) return '';
  return v;
}

function normalizePhoneImport(val: string): string {
  const v = normalizeImportValue(val);
  if (!v) return '';
  const first = v.split(/:::+|\/|\||;|\n/)[0] || v;
  return first.trim();
}

function looksLikeGarbage(val: string): boolean {
  if (!val) return false;
  const v = val.trim();
  // All dashes, dots, underscores → "--------", "........"
  if (/^[-.\s_]+$/.test(v)) return true;
  // Has 2+ commas → address fragment "TZ,Street,City,,"
  if ((v.match(/,/g) || []).length >= 2) return true;
  // Purely numeric → postal code "00100", "3463"
  if (/^\d+$/.test(v)) return true;
  // Very short code-like value ≤5 chars → "10-B", "A4", "00100"
  if (v.length <= 5 && /^[\dA-Z\-]+$/i.test(v)) return true;
  // European hyphenated postal code + city → "03-972 Warsaw"
  if (/^\d+[-]\d+\s+[A-Z]/.test(v)) return true;
  // 4-5 digit postal code + single word → "0153 Oslo", "10119 Berlin", "08001 Barcelona"
  if (/^\d{4,5}\s+\w+$/.test(v)) return true;
  // Postal code with 2-letter country/area code → "1057 CD Amsterdam", "3572AN Utrecht"
  if (/^\d{3,5}\s*[A-Z]{2}\s+/i.test(v)) return true;
  // Starts with digit AND contains a street keyword → "344 Ghuba Road", "3 Niagara Street", "1st Floor Block"
  if (/^\d/.test(v) && STREET_KEYWORDS.test(v)) return true;
  return false;
}

interface CsvImportModalProps {
  onImported: (customers: Customer[]) => void;
  onClose: () => void;
}

const CsvImportModal: React.FC<CsvImportModalProps> = ({ onImported, onClose }) => {
  const [step, setStep] = useState<'upload' | 'map' | 'importing' | 'done'>('upload');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<MaosFieldKey, string>>({} as any);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [result, setResult] = useState<{ created: number; skipped: number; errors: { row: number; name: string; reason: string }[] } | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      const { headers: h, rows: r } = parseCsv(text);
      setHeaders(h);
      setRows(r);
      setMapping(guessMapping(h));
      setStep('map');
    };
    reader.readAsText(file);
  };

  // Build a preview row using current mapping
  const preview = rows.slice(0, 3).map(row => {
    const obj: Record<string, string> = {};
    MAOS_FIELDS.forEach(f => {
      const col = mapping[f.key];
      if (col) {
        const idx = headers.indexOf(col);
        obj[f.key] = idx >= 0 ? (row[idx] || '') : '';
      }
    });
    // auto-build display name if not mapped but first+last are
    if (!obj.name && (obj.firstName || obj.lastName)) {
      obj.name = `${obj.firstName || ''} ${obj.lastName || ''}`.trim();
    }
    if (!obj.name && obj.company) obj.name = obj.company;
    return obj;
  });

  const doImport = async () => {
    setStep('importing');
    // Build contacts array
    const contacts = rows.map(row => {
    const get = (key: MaosFieldKey) => {
      const col = mapping[key];
      if (!col) return '';
      const idx = headers.indexOf(col);
      return idx >= 0 ? normalizeImportValue(row[idx] || '') : '';
    };
      const firstName = get('firstName');
      const lastName = get('lastName');
      const company = get('company');
      const rawName = get('name');
      // Build best display name: prefer first+last, then company, then mapped name
      // Skip if it looks like an address fragment
      let name = '';
      if (firstName || lastName) name = `${firstName} ${lastName}`.trim();
      if (!name && rawName && !looksLikeGarbage(rawName)) name = rawName;
      if (!name && company && !looksLikeGarbage(company)) name = company;
      return {
        name,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        company: (company && !looksLikeGarbage(company)) ? company : undefined,
        email: get('email') || undefined,
        phoneNumber: normalizePhoneImport(get('phoneNumber')) || undefined,
        tin: get('tin') || undefined,
        vrn: get('vrn') || undefined,
        customerType: company ? 'BUSINESS' : (firstName || lastName ? 'INDIVIDUAL' : 'BUSINESS'),
      };
    }).filter(c => c.name);

    try {
      const res = await api.post('/contacts/import', { contacts, skipDuplicates });
      const data = res.data;
      setResult(data);
      setStep('done');
      // Reload customer list
      const freshList = await contactsApi.getAll();
      onImported(freshList);
    } catch (err: any) {
      setResult({ created: 0, skipped: 0, errors: [{ row: 0, name: '', reason: err?.response?.data?.error || err.message }] });
      setStep('done');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Upload className="h-4 w-4 text-blue-600" />
            <span className="font-semibold text-gray-900 text-sm">Import Contacts from CSV</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-0 px-5 py-2 bg-gray-50 border-b border-gray-100 text-[11px]">
          {(['upload', 'map', 'done'] as const).map((s, i) => (
            <React.Fragment key={s}>
              <span className={`px-2 py-0.5 rounded font-medium ${step === s || (step === 'importing' && s === 'done') ? 'bg-blue-600 text-white' : (i < ['upload','map','done'].indexOf(step === 'importing' ? 'done' : step) ? 'text-blue-600' : 'text-gray-400')}`}>
                {i + 1}. {s === 'upload' ? 'Upload' : s === 'map' ? 'Map Columns' : 'Result'}
              </span>
              {i < 2 && <ChevronRight className="h-3 w-3 text-gray-300 mx-1" />}
            </React.Fragment>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5">

          {/* STEP 1 — Upload */}
          {step === 'upload' && (
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
              className={`border-2 border-dashed rounded-xl flex flex-col items-center justify-center py-16 cursor-pointer transition-colors ${dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
              onClick={() => { const inp = document.createElement('input'); inp.type = 'file'; inp.accept = '.csv,text/csv'; inp.onchange = (e: any) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }; inp.click(); }}
            >
              <Upload className="h-10 w-10 text-gray-300 mb-3" />
              <p className="text-sm font-medium text-gray-600">Drop your CSV file here or click to browse</p>
              <p className="text-xs text-gray-400 mt-1">Supports Google Contacts, Outlook, and any standard CSV export</p>
            </div>
          )}

          {/* STEP 2 — Map */}
          {step === 'map' && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">{rows.length} rows detected. Match your CSV columns to MA-OS contact fields below.</p>

              <div className="grid gap-2">
                {MAOS_FIELDS.map(f => (
                  <div key={f.key} className="flex items-center gap-3">
                    <span className="w-44 text-xs font-medium text-gray-700 flex-shrink-0">
                      {f.label}
                    </span>
                    <select
                      value={mapping[f.key] || ''}
                      onChange={e => setMapping(m => ({ ...m, [f.key]: e.target.value }))}
                      className="flex-1 border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      <option value="">— skip —</option>
                      {headers.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                    {mapping[f.key] && (
                      <span className="text-[10px] text-gray-400 w-28 truncate flex-shrink-0">
                        e.g. {rows[0]?.[headers.indexOf(mapping[f.key])] || '—'}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Preview */}
              {preview.length > 0 && (
                <div className="mt-4">
                  <p className="text-[11px] font-medium text-gray-500 mb-1.5 uppercase tracking-wider">Preview (first 3 rows)</p>
                  <div className="overflow-x-auto rounded-lg border border-gray-100">
                    <table className="w-full text-[11px]">
                      <thead className="bg-gray-50">
                        <tr>
                          {MAOS_FIELDS.filter(f => mapping[f.key]).map(f => (
                            <th key={f.key} className="px-2.5 py-1.5 text-left font-medium text-gray-500 whitespace-nowrap">{f.label.replace(' *','')}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.map((row, i) => (
                          <tr key={i} className="border-t border-gray-50">
                            {MAOS_FIELDS.filter(f => mapping[f.key]).map(f => (
                              <td key={f.key} className="px-2.5 py-1.5 text-gray-700 whitespace-nowrap max-w-[140px] truncate">{row[f.key] || <span className="text-gray-300">—</span>}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <label className="flex items-center gap-2 text-xs text-gray-600 mt-2 cursor-pointer">
                <input type="checkbox" checked={skipDuplicates} onChange={e => setSkipDuplicates(e.target.checked)} className="rounded" />
                Skip contacts that already exist (matched by email or phone)
              </label>
            </div>
          )}

          {/* STEP 3 — Importing */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <RefreshCw className="h-8 w-8 text-blue-600 animate-spin" />
              <p className="text-sm text-gray-600">Importing {rows.length} contacts…</p>
            </div>
          )}

          {/* STEP 4 — Done */}
          {step === 'done' && result && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">{result.created}</p>
                  <p className="text-xs text-green-600 mt-0.5">Created</p>
                </div>
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-center">
                  <p className="text-2xl font-bold text-amber-700">{result.skipped}</p>
                  <p className="text-xs text-amber-600 mt-0.5">Skipped (duplicate)</p>
                </div>
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center">
                  <p className="text-2xl font-bold text-red-700">{result.errors.length}</p>
                  <p className="text-xs text-red-600 mt-0.5">Errors</p>
                </div>
              </div>
              {result.errors.length > 0 && (
                <div className="rounded-lg border border-red-100 bg-red-50 p-3 max-h-48 overflow-y-auto">
                  <p className="text-xs font-medium text-red-700 mb-2">Errors</p>
                  {result.errors.map((e, i) => (
                    <p key={i} className="text-xs text-red-600">Row {e.row} — {e.name || 'unknown'}: {e.reason}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100">
          {step === 'done' ? (
            <button onClick={onClose} className="px-4 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700">Close</button>
          ) : step === 'map' ? (
            <>
              <button onClick={() => setStep('upload')} className="px-4 py-2 text-xs border border-gray-200 rounded-lg hover:bg-gray-50">Back</button>
              <button
                onClick={doImport}
                disabled={!rows.length || !(mapping.name || mapping.company || mapping.firstName || mapping.lastName)}
                className="px-4 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-40"
              >
                Import {rows.length} contacts
              </button>
            </>
          ) : step === 'upload' ? (
            <button onClick={onClose} className="px-4 py-2 text-xs border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const FILTER_STATUSES = ['ALL', 'LEAD', 'PROSPECT', 'ACTIVE', 'ON_HOLD', 'INACTIVE'] as const;

const ContactsHub: React.FC<ContactsHubProps> = ({ customers, setCustomers, onNavigateToWA, onNavigateToEmail, onNavigateToQuote }) => {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [showFullCreate, setShowFullCreate] = useState(false);
  const [editingContact, setEditingContact] = useState<Customer | null>(null);
  const [showMerge, setShowMerge] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [clearingImport, setClearingImport] = useState(false);
  const [loading, setLoading] = useState(false);
  const appliedHashIntentRef = React.useRef<string>('');

  React.useEffect(() => {
    const applyHashIntent = () => {
      const intent = readContactsHubHashIntent();
      const nextKey = JSON.stringify(intent);
      if (appliedHashIntentRef.current === nextKey) return;
      if (intent.search) setSearch(intent.search);
      if (intent.contactId) setSelectedId(intent.contactId);
      if (intent.edit && intent.contactId) {
        const customer = customers.find((row) => row.id === intent.contactId) || null;
        if (!customer) return;
        setEditingContact(customer);
        setShowFullCreate(true);
      }
      appliedHashIntentRef.current = nextKey;
    };

    applyHashIntent();
    window.addEventListener('hashchange', applyHashIntent);
    return () => window.removeEventListener('hashchange', applyHashIntent);
  }, [customers]);

  const importedCount = customers.filter(c => c.source === 'IMPORT').length;

  const handleClearImported = async () => {
    if (!window.confirm(`Remove all ${importedCount} imported contacts? This cannot be undone.`)) return;
    setClearingImport(true);
    try {
      await contactsApi.clearImported();
      setCustomers(prev => prev.filter(c => c.source !== 'IMPORT'));
      if (selectedId && customers.find(c => c.id === selectedId && c.source === 'IMPORT')) {
        setSelectedId(null);
      }
    } finally {
      setClearingImport(false);
    }
  };

  const filtered = customers.filter(c => {
    if (filterStatus !== 'ALL' && c.status !== filterStatus) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.phoneNumber || '').includes(q) ||
      (c.company || '').toLowerCase().includes(q)
    );
  });

  // Stats for header
  const counts = {
    lead: customers.filter(c => c.status === 'LEAD').length,
    prospect: customers.filter(c => c.status === 'PROSPECT').length,
    active: customers.filter(c => c.status === 'ACTIVE').length,
  };

  const handleCreated = (c: Customer) => {
    setCustomers(prev => [c, ...prev]);
    setSelectedId(c.id);
    setShowQuickCreate(false);
  };

  const handleFullSaved = (c: Customer) => {
    if (editingContact) {
      setCustomers(prev => prev.map(x => x.id === c.id ? c : x));
    } else {
      setCustomers(prev => [c, ...prev]);
      setSelectedId(c.id);
    }
    setShowFullCreate(false);
    setEditingContact(null);
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-5 py-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-blue-600" />
            <h1 className="text-base font-semibold text-gray-900">Contacts</h1>
            <span className="text-xs text-gray-400">{customers.length} total</span>
          </div>
          <div className="flex items-center gap-2">
            {importedCount > 0 && (
              <button
                onClick={handleClearImported}
                disabled={clearingImport}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-red-200 text-red-600 text-xs rounded-lg hover:bg-red-50 disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {clearingImport ? 'Removing…' : `Remove ${importedCount} Imported`}
              </button>
            )}
            <button
              onClick={() => setShowImport(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-600 text-xs rounded-lg hover:bg-gray-50"
            >
              <Upload className="h-3.5 w-3.5" />Import CSV
            </button>
            <button
              onClick={() => setShowFullCreate(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-3.5 w-3.5" />New Contact
            </button>
          </div>
        </div>

        {/* Stats pills */}
        <div className="flex gap-2 mb-3">
          {[
            { label: 'Leads', count: counts.lead, color: 'text-purple-600 bg-purple-50', status: 'LEAD' },
            { label: 'Prospects', count: counts.prospect, color: 'text-blue-600 bg-blue-50', status: 'PROSPECT' },
            { label: 'Active', count: counts.active, color: 'text-green-600 bg-green-50', status: 'ACTIVE' },
          ].map(s => (
            <button
              key={s.status}
              onClick={() => setFilterStatus(prev => prev === s.status ? 'ALL' : s.status)}
              className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium transition-all ${filterStatus === s.status ? s.color + ' ring-1 ring-current' : 'bg-gray-100 text-gray-600'}`}
            >
              {s.label} <span className="font-bold">{s.count}</span>
            </button>
          ))}
        </div>

        {/* Search + filter */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, phone, email, company…"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            {FILTER_STATUSES.map(s => <option key={s} value={s}>{s === 'ALL' ? 'All statuses' : STATUS_LABELS[s]}</option>)}
          </select>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* List */}
        <div className={`flex flex-col overflow-hidden ${selectedId ? 'w-80 flex-shrink-0 border-r border-gray-200' : 'flex-1'}`}>
          <div className="flex-1 overflow-y-auto bg-white">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                <Users className="h-8 w-8 mb-2 opacity-30" />
                <p className="text-sm">No contacts found</p>
              </div>
            ) : (
              filtered.map(c => (
                <ListRow key={c.id} customer={c} selected={c.id === selectedId} onClick={() => setSelectedId(prev => prev === c.id ? null : c.id)} />
              ))
            )}
          </div>
        </div>

        {/* Profile panel */}
        {selectedId && (
          <div className="flex-1 overflow-hidden">
            <ProfilePanel
              key={selectedId}
              customerId={selectedId}
              onClose={() => setSelectedId(null)}
              onNavigateToWA={onNavigateToWA}
              onNavigateToEmail={onNavigateToEmail}
              onNavigateToQuote={onNavigateToQuote}
              onMergeRequest={() => setShowMerge(true)}
              onEditRequest={(c) => { setEditingContact(c); setShowFullCreate(true); }}
            />
          </div>
        )}
      </div>

      {/* Quick create modal (used inline from charter/WA) */}
      {showQuickCreate && (
        <QuickCreateCustomerModal
          onCreated={handleCreated}
          onClose={() => setShowQuickCreate(false)}
        />
      )}

      {/* Full contact form (create or edit) */}
      {showFullCreate && (
        <FullContactFormModal
          initial={editingContact || undefined}
          onSaved={handleFullSaved}
          onClose={() => { setShowFullCreate(false); setEditingContact(null); }}
        />
      )}

      {/* CSV Import modal */}
      {showImport && (
        <CsvImportModal
          onImported={newList => { setCustomers(newList); setShowImport(false); }}
          onClose={() => setShowImport(false)}
        />
      )}

      {/* Merge modal */}
      {showMerge && selectedId && (
        <MergeModal
          winner={customers.find(c => c.id === selectedId)!}
          customers={customers}
          onMerged={(winnerId, loserId) => {
            setCustomers(prev => prev.filter(c => c.id !== loserId));
            setShowMerge(false);
          }}
          onClose={() => setShowMerge(false)}
        />
      )}
    </div>
  );
};

export default ContactsHub;
